// 🧪 SSOT 통합 검증 테스트 페이지
// [배경] SSOT 통합이 제대로 됐는지 검증하는 테스트 페이지
// [목적] 보안 제외, SSOT 정합성만 검증

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Science as ScienceIcon,
  Search as ScanIcon,
  PlayArrow as WorkflowIcon,
  Code as PlaygroundIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// 탭 컴포넌트 임포트
import ScanTab from './components/ssotTestLab/ScanTab';
import WorkflowTab from './components/ssotTestLab/WorkflowTab';
import PlaygroundTab from './components/ssotTestLab/PlaygroundTab';

// 서비스 임포트
import { 
  SSOTTestService, 
  ScanResult, 
  WorkflowTestResult, 
  ScorecardResult,
  TestRunContext
} from '../../services/ssotTestService';

// 탭 인덱스 타입
type TabIndex = 0 | 1 | 2;

/**
 * 🧪 SSOT 테스트 랩 페이지
 */
const SSOTTestLabPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // === 상태 ===
  const [activeTab, setActiveTab] = useState<TabIndex>(0);
  const [testRun, setTestRun] = useState<TestRunContext | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [workflowResults, setWorkflowResults] = useState<WorkflowTestResult[]>([]);
  const [scorecard, setScorecard] = useState<ScorecardResult | null>(null);
  const [loading, setLoading] = useState(false);
  
  // === 테스트 세션 관리 ===
  const handleStartTestRun = useCallback(() => {
    const context = SSOTTestService.startTestRun();
    setTestRun(context);
    setScanResult(null);
    setWorkflowResults([]);
    setScorecard(null);
  }, []);
  
  const handleEndTestRun = useCallback(async () => {
    if (testRun) {
      setLoading(true);
      try {
        // 테스트 데이터 정리
        await SSOTTestService.cleanupTestData(testRun.testRunId);
      } catch (error) {
        console.error('테스트 데이터 정리 실패:', error);
      }
      SSOTTestService.endTestRun();
      setTestRun(null);
      setLoading(false);
    }
  }, [testRun]);
  
  // === 스캔 결과 업데이트 콜백 ===
  const handleScanComplete = useCallback((result: ScanResult) => {
    setScanResult(result);
    
    // 스코어카드 재계산
    if (workflowResults.length > 0) {
      const newScorecard = SSOTTestService.calculateScorecard(result, workflowResults);
      setScorecard(newScorecard);
    }
  }, [workflowResults]);
  
  // === 워크플로우 결과 업데이트 콜백 ===
  const handleWorkflowComplete = useCallback((results: WorkflowTestResult[]) => {
    setWorkflowResults(prev => [...prev, ...results]);
    
    // 스코어카드 재계산
    if (scanResult) {
      const allResults = [...workflowResults, ...results];
      const newScorecard = SSOTTestService.calculateScorecard(scanResult, allResults);
      setScorecard(newScorecard);
    }
  }, [scanResult, workflowResults]);
  
  // === 자동 채점 재계산 ===
  const handleRecalculateScorecard = useCallback(async () => {
    setLoading(true);
    try {
      // 새 스캔 실행
      const newScanResult = await SSOTTestService.runFullScan(!!testRun);
      setScanResult(newScanResult);
      
      // 스코어카드 계산
      const newScorecard = SSOTTestService.calculateScorecard(newScanResult, workflowResults);
      setScorecard(newScorecard);
    } catch (error) {
      console.error('채점 실패:', error);
    }
    setLoading(false);
  }, [testRun, workflowResults]);
  
  // === 탭 변경 핸들러 ===
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: TabIndex) => {
    setActiveTab(newValue);
  }, []);
  
  // === 상단 스코어카드 렌더링 ===
  const renderScorecard = useMemo(() => {
    if (!scorecard) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          스캔 또는 워크플로우 테스트를 실행하면 자동 채점 결과가 표시됩니다.
        </Alert>
      );
    }
    
    return (
      <Card 
        sx={{ 
          mb: 3,
          bgcolor: scorecard.passed ? 'success.50' : 'error.50',
          border: '2px solid',
          borderColor: scorecard.passed ? 'success.main' : 'error.main'
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {scorecard.passed ? (
                <PassIcon sx={{ fontSize: 48, color: 'success.main' }} />
              ) : (
                <FailIcon sx={{ fontSize: 48, color: 'error.main' }} />
              )}
              <Box>
                <Typography variant="h5" fontWeight={800} color={scorecard.passed ? 'success.main' : 'error.main'}>
                  {scorecard.passed ? 'ALL PASS' : 'FAIL'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  SSOT 정합성 자동 채점 결과
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRecalculateScorecard}
              disabled={loading}
              size="small"
            >
              재채점
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {scorecard.details.map((detail, idx) => (
              <Chip
                key={idx}
                label={detail}
                size="small"
                color={detail.startsWith('✅') ? 'success' : 'error'}
                variant={detail.startsWith('✅') ? 'outlined' : 'filled'}
              />
            ))}
          </Box>
          
          {/* 상세 수치 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">비정규 ID</Typography>
              <Typography variant="h6" fontWeight={700}>{scorecard.nonCanonicalCount}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">고아 가격</Typography>
              <Typography variant="h6" fontWeight={700}>{scorecard.orphanCount}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">중복 구성</Typography>
              <Typography variant="h6" fontWeight={700}>{scorecard.duplicateCount}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">ID 매칭률</Typography>
              <Typography variant="h6" fontWeight={700}>{scorecard.idMatchRate.toFixed(1)}%</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }, [scorecard, loading, handleRecalculateScorecard]);

  return (
    <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 1400, mx: 'auto' }}>
      {/* 페이지 헤더 */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <ScienceIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={800}>
              SSOT 테스트 랩
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID 체계 정합성 검증 및 테스트 (보안 제외)
            </Typography>
          </Box>
        </Box>
        
        {/* 테스트 세션 상태 */}
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            mt: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            bgcolor: testRun ? 'warning.50' : 'grey.50'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={testRun ? '🧪 테스트 모드 활성' : '⏸️ 테스트 대기'}
              color={testRun ? 'warning' : 'default'}
              variant="filled"
            />
            {testRun && (
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                RunID: {testRun.testRunId}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!testRun ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartTestRun}
                startIcon={<ScienceIcon />}
              >
                테스트 시작
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                onClick={handleEndTestRun}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : undefined}
              >
                {loading ? '정리 중...' : '테스트 종료 & 정리'}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
      
      {/* 자동 채점 스코어카드 */}
      {renderScorecard}
      
      {/* 탭 네비게이션 */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<ScanIcon />} 
            label="스캔" 
            iconPosition="start"
            sx={{ minHeight: 56 }}
          />
          <Tab 
            icon={<WorkflowIcon />} 
            label="워크플로우" 
            iconPosition="start"
            sx={{ minHeight: 56 }}
            disabled={!testRun}
          />
          <Tab 
            icon={<PlaygroundIcon />} 
            label="실험실" 
            iconPosition="start"
            sx={{ minHeight: 56 }}
          />
        </Tabs>
        
        {/* 탭 콘텐츠 */}
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          {activeTab === 0 && (
            <ScanTab 
              useTestCollection={!!testRun}
              onScanComplete={handleScanComplete}
              scanResult={scanResult}
            />
          )}
          {activeTab === 1 && (
            <WorkflowTab 
              testRun={testRun}
              onWorkflowComplete={handleWorkflowComplete}
              workflowResults={workflowResults}
            />
          )}
          {activeTab === 2 && (
            <PlaygroundTab />
          )}
        </Box>
      </Paper>
      
      {/* 워크플로우 테스트 비활성 안내 */}
      {activeTab === 1 && !testRun && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          워크플로우 탭을 사용하려면 먼저 "테스트 시작" 버튼을 클릭하여 테스트 세션을 시작해주세요.
          테스트 데이터는 운영 데이터와 격리된 테스트 컬렉션에 저장됩니다.
        </Alert>
      )}
    </Box>
  );
};

export default SSOTTestLabPage;

